// Missions et récompenses : trois premières missions, des points d'expérience et des badges.
// Logique pure (testable) + magasin persistant. Règles : une récompense (mission + palier)
// n'est attribuée qu'une fois, même après rechargement ; les paliers distinguent la
// découverte au clavier d'essai (ou à la synthèse) des réussites musicales sur un instrument
// (micro ou MIDI) ; seules les notes reconnues avec certitude comptent ; la mission « tempo »
// s'appuie sur la règle de réussite existante (isPassed), sans l'assouplir.

import type { SessionSummary } from '../engine/session';
import type { InputKind } from '../engine/types';
import { measureCount, type Score } from '../music/model';
import { isPassed } from './progress';
import { Store } from './store';

export type MissionId = 'five-notes' | 'wait-exercise' | 'tempo-pass';
export type RewardTier = 'discovery' | 'instrument';

export interface MissionDef {
  id: MissionId;
  /** Titre court. */
  title: string;
  /** Consigne en une phrase. */
  goal: string;
  /** Condition précise, pour l'aide et le détail. */
  detail: string;
  xp: Record<RewardTier, number>;
  badge: Record<RewardTier, string>;
  icon: string;
  /** Réservée aux instruments (micro ou MIDI) : jamais attribuée en découverte. */
  instrumentOnly: boolean;
  /** Contenu conseillé pour la relever, et mode à présélectionner. */
  suggestedScoreId: string;
  suggestedMode: 'wait' | 'tempo';
}

/** Un exercice « court » : quatre mesures au plus. */
export const SHORT_EXERCISE_MAX_MEASURES = 4;
export const FIVE_NOTES_TARGET = 5;
export const XP_PER_LEVEL = 100;

export const MISSIONS: readonly MissionDef[] = [
  {
    id: 'five-notes',
    title: 'Cinq notes justes',
    goal: 'Jouez cinq notes attendues, reconnues justes, pendant un exercice.',
    detail: 'Au cours d’une même séance, cinq notes attendues jouées à la bonne hauteur et reconnues avec certitude (en avance ou en retard, elles comptent aussi). Les détections incertaines ne comptent jamais.',
    xp: { discovery: 10, instrument: 25 },
    badge: { discovery: 'Premières notes · découverte', instrument: 'Premières notes' },
    icon: '🎵',
    instrumentOnly: false,
    suggestedScoreId: 'md-cinq-doigts',
    suggestedMode: 'wait',
  },
  {
    id: 'wait-exercise',
    title: 'Un exercice en entier',
    goal: 'Terminez un exercice court en mode attente.',
    detail: `Aller jusqu’au bout d’un exercice de ${SHORT_EXERCISE_MAX_MEASURES} mesures au plus, en mode attente, sans l’arrêter en route.`,
    xp: { discovery: 20, instrument: 50 },
    badge: { discovery: 'Premier exercice · découverte', instrument: 'Premier exercice' },
    icon: '🏁',
    instrumentOnly: false,
    suggestedScoreId: 'md-cinq-doigts',
    suggestedMode: 'wait',
  },
  {
    id: 'tempo-pass',
    title: 'Réussite au tempo',
    goal: 'Réussissez un exercice débutant en mode tempo imposé, sur votre piano.',
    detail: 'Exercice de niveau découverte, mode tempo imposé, sur un instrument (micro ou MIDI), avec les critères de réussite habituels : 80 % de notes évaluables, 90 % de notes justes, 80 % en rythme, précision 90 %, tempo à 80 % de la cible au moins. Le clavier d’essai ne valide pas.',
    xp: { discovery: 0, instrument: 100 },
    badge: { discovery: '', instrument: 'Premier succès au tempo' },
    icon: '⭐',
    instrumentOnly: true,
    suggestedScoreId: 'md-cinq-doigts',
    suggestedMode: 'tempo',
  },
];

export function missionById(id: MissionId): MissionDef {
  const def = MISSIONS.find((m) => m.id === id);
  if (!def) throw new Error(`mission inconnue : ${id}`);
  return def;
}

export interface Award {
  id: MissionId;
  tier: RewardTier;
  xp: number;
  at: string;
  scoreId: string;
}

export interface MissionsState {
  version: 1;
  xp: number;
  awards: Award[];
}

export const DEFAULT_MISSIONS: MissionsState = { version: 1, xp: 0, awards: [] };

/** Palier d'une séance : instrument seulement si toutes les notes viennent du micro ou du MIDI. */
export function tierOf(sources: readonly InputKind[]): RewardTier {
  return sources.length > 0 && sources.every((s) => s === 'mic' || s === 'midi') ? 'instrument' : 'discovery';
}

export function hasAward(state: MissionsState, id: MissionId, tier: RewardTier): boolean {
  return state.awards.some((a) => a.id === id && a.tier === tier);
}

/** Attribue une récompense si elle ne l'a pas déjà été ; renvoie le nouvel état (l'ancien n'est pas modifié). */
export function grant(state: MissionsState, id: MissionId, tier: RewardTier, scoreId: string, at: Date = new Date()): { state: MissionsState; award: Award | null } {
  const def = missionById(id);
  if (def.instrumentOnly && tier === 'discovery') return { state, award: null };
  if (hasAward(state, id, tier)) return { state, award: null };
  const award: Award = { id, tier, xp: def.xp[tier], at: at.toISOString(), scoreId };
  return { state: { ...state, xp: state.xp + award.xp, awards: [...state.awards, award] }, award };
}

export interface NotesContext {
  /** Notes attendues jouées à la bonne hauteur et reconnues avec certitude depuis le début de la séance. */
  sureHits: number;
  sources: readonly InputKind[];
  scoreId: string;
}

/** Mission « cinq notes » : au moment précis où la cinquième note sûre arrive. */
export function applyNotes(state: MissionsState, ctx: NotesContext, at: Date = new Date()): { state: MissionsState; awards: Award[] } {
  if (ctx.sureHits !== FIVE_NOTES_TARGET) return { state, awards: [] };
  const r = grant(state, 'five-notes', tierOf(ctx.sources), ctx.scoreId, at);
  return { state: r.state, awards: r.award ? [r.award] : [] };
}

export interface SessionEndContext {
  score: Score;
  summary: SessionSummary;
  /** La séance est allée jusqu'au bout (pas arrêtée par le bouton). */
  completed: boolean;
  targetTempo: number;
}

export function isShortExercise(score: Score): boolean {
  return score.kind === 'exercise' && measureCount(score) <= SHORT_EXERCISE_MAX_MEASURES;
}

/** Missions accomplies par une séance terminée. */
export function applySessionEnd(state: MissionsState, ctx: SessionEndContext, at: Date = new Date()): { state: MissionsState; awards: Award[] } {
  const { score, summary } = ctx;
  const tier = tierOf(summary.sources);
  const awards: Award[] = [];
  let next = state;
  if (ctx.completed && summary.mode === 'wait' && isShortExercise(score) && summary.expected > 0 && summary.hits >= summary.expected) {
    const r = grant(next, 'wait-exercise', tier, score.id, at);
    next = r.state;
    if (r.award) awards.push(r.award);
  }
  // La règle de réussite existante décide seule ; elle exige déjà un instrument.
  if (summary.mode === 'tempo' && score.kind === 'exercise' && score.level === 1 && isPassed(summary, ctx.targetTempo)) {
    const r = grant(next, 'tempo-pass', 'instrument', score.id, at);
    next = r.state;
    if (r.award) awards.push(r.award);
  }
  return { state: next, awards };
}

export interface LevelInfo {
  level: number;
  /** Points acquis dans le niveau courant. */
  into: number;
  /** Points nécessaires pour passer au niveau suivant. */
  next: number;
}

export function levelOf(xp: number): LevelInfo {
  const level = Math.floor(Math.max(0, xp) / XP_PER_LEVEL) + 1;
  return { level, into: Math.max(0, xp) - (level - 1) * XP_PER_LEVEL, next: XP_PER_LEVEL };
}

export type MissionStatus = 'todo' | 'discovery' | 'instrument';

/** État d'une mission : non faite, faite en découverte seulement, ou réussie sur instrument. */
export function missionStatus(state: MissionsState, id: MissionId): MissionStatus {
  if (hasAward(state, id, 'instrument')) return 'instrument';
  if (hasAward(state, id, 'discovery')) return 'discovery';
  return 'todo';
}

/** Prochaine mission à proposer : la première non réussie sur instrument, en privilégiant celles jamais faites. */
export function nextMission(state: MissionsState): MissionDef | null {
  return MISSIONS.find((m) => missionStatus(state, m.id) === 'todo') ?? MISSIONS.find((m) => missionStatus(state, m.id) !== 'instrument') ?? null;
}

export function migrateMissions(raw: unknown): MissionsState | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<MissionsState>;
  const awards = Array.isArray(r.awards)
    ? r.awards.filter((a): a is Award => !!a && typeof a === 'object' && MISSIONS.some((m) => m.id === (a as Award).id) && ((a as Award).tier === 'discovery' || (a as Award).tier === 'instrument'))
    : [];
  // Les points sont recalculés depuis les récompenses : une valeur incohérente ne survit pas.
  const xp = awards.reduce((sum, a) => sum + (typeof a.xp === 'number' ? a.xp : 0), 0);
  return { version: 1, xp, awards };
}

export const missionsStore = new Store<MissionsState>('pianohome.missions.v1', DEFAULT_MISSIONS, { migrate: migrateMissions });

/** À appeler à chaque note attendue jouée juste (sûre) pendant une séance ; renvoie les récompenses obtenues. */
export function recordSureHit(ctx: NotesContext): Award[] {
  const r = applyNotes(missionsStore.get(), ctx);
  if (r.awards.length > 0) missionsStore.set(() => r.state);
  return r.awards;
}

/** À appeler à la fin d'une séance ; renvoie les récompenses obtenues. */
export function recordSessionEnd(ctx: SessionEndContext): Award[] {
  const r = applySessionEnd(missionsStore.get(), ctx);
  if (r.awards.length > 0) missionsStore.set(() => r.state);
  return r.awards;
}
